import { channelInfo, channelLegendRichInfo, dealwithChannelChartKey } from '@op/components/ChannelLabel';
import EChartCommon from '@op/components/EChartCommon';
import { channelColorMap, chartAllColors, NO_DATE } from '@op/constants';
import useLanguage from '@op/hooks/useLanguage';
import { echartsContent, getPercentageInfo, transLanguage } from '@op/utils';
import { dealWithOthersName } from '@op/utils/languageValue';
import React, { useMemo, useRef } from 'react';
import { getSpecialLabelName } from '../../utils';
import { formatChartParamsDate } from '@op/components/ChartCommon/formatter';
import { formatTimeToShow } from '@op/utils/time';
import { measureTextWidth } from '@op/pages/Overview/KeyMetrics/MentionsSentiment/Channel';
import { reviewsFormat } from '@op/utils/value';
import { IRealtimeCommunityChart } from '../../type';

export default function PieChart(props: IRealtimeCommunityChart) {
  const { chartType, formatValue = reviewsFormat, data, byType, filter, title, height = 366 } = props;
  const eChartsRef = useRef<any>(null);
  const { formatMessage } = useLanguage();
  const chartRealType = useMemo(() => {
    if (chartType === 'common-bar') {
      return 'bar';
    }
    return chartType;
  }, [chartType]);

  const options = useMemo(() => {
    const { pieData, pieTotalData, legend } = data || {};
    const seriesData = pieData.map((val, index) => {
      const { name } = val;
      const color = channelColorMap[name] || chartAllColors[index];
      return {
        ...val,
        ...(chartType === 'pie' ? {
          itemStyle: {
            color
          }
        } : {}),
      };
    });
    const options = {
      series: {
        data: seriesData,
        type: chartRealType,
        label: {
          color: '#262832',
          alignTo: 'edge',
          formatter: (params: any) => {
            const percent = getPercentageInfo(params?.value, pieTotalData, 2);
            let name = '';
            let icon = '';
            const label = getSpecialLabelName(params.name);
            if (byType === 'by_language') {
              name = dealWithOthersName(params.name, 'language');
            }
            if (byType === 'by_channel') {
              icon = `{${dealwithChannelChartKey(params.name)}|}`;
              name = dealWithOthersName(params.name, 'channel');
            }

            if (!name) {
              name = formatMessage({ id: label });
            }

            let percentText = formatValue(params.value);
            if (params?.value) {
              percentText += `(${percent})`;
            }

            return `${icon}{value| ${name}}: {a|${percentText}}\n{hrLine|}\n{a|}`;
          },
          edgeDistance: 10,
          rich: {
            a: {
              // color: '#999',
              lineHeight: 20, // 设置最后一行空数据高度，为了能让延长线与hr线对接起来
              align: 'center',
            },
            hrLine: {
              // 设置hr是为了让中间线能够自适应长度
              borderColor: 'auto', // hr的颜色为auto时候会主动显示颜色的
              width: '105%',
              borderWidth: 0.5,
              height: 0.5,
            },
            per: {
              // 用百分比数据来调整下数字位置，显的好看些。如果不设置，formatter最后一行的空数据就不需要
              padding: [4, 0],
            },
            ...channelLegendRichInfo(16, 16),
          },
        },
        labelLayout: (params: any) => {
          const isLeft =
            params.labelRect.x <
            eChartsRef?.current?.getEchartsInstance?.()?.getWidth() / 2;
          const points = params.labelLinePoints;
          // Update the end point.
          points[2][0] = isLeft
            ? params.labelRect.x
            : params.labelRect.x + params.labelRect.width;
          return {
            labelLinePoints: points,
          };
        },
        emphasis: {
          focus: 'self',
          label: {
            opacity: 0.2,
          },
          labelLine: {
            lineStyle: {
              opacity: 0.2,
            },
          },
        },
        blur: {
          label: { opacity: 0.2 },
          itemStyle: {
            opacity: 0.2,
          },
          labelLine: {
            lineStyle: {
              opacity: 0.2,
            },
          },
        },
      },
      legend: {
        data: legend,
        formatter: (params: any) => {
          if (byType === 'by_channel' && params !== 'Others') {
            const { name } = channelInfo(params);
            return `{${dealwithChannelChartKey(params)}|} {name| ${name}}`;
          }
          if (byType === 'by_language') {
            return dealWithOthersName(params, 'language');
          }
          const label = getSpecialLabelName(params);
          return formatMessage({ id: label });
        },
      },
      tooltip: {
        borderWidth: 0,
        borderRadius: 6,
        padding: 0,
        formatter: (params: any) =>
          formatChartParamsDate({
            params,
            listTitleFunc: (item: any) => {
              const isNoOther = item.name !== 'Others';
              if (byType === 'by_channel') {
                const { img, name } = channelInfo(item.name);
                return `<span class="dbo-chart-formatter-img"><img src=${img} class="chart-common-formatter-img" /> ${isNoOther ? name : formatMessage({ id: item.name })}<span>`;
              }
              if (byType === 'by_language' && isNoOther) {
                return transLanguage(item.name);
              }
              const label = getSpecialLabelName(item.name);
              return formatMessage({ id: label || NO_DATE.EMPTY });
            },
            tablesConfigFunc: (records: any) => {
              const columns = [
                {
                  key: 'text',
                  header: formatTimeToShow(filter),
                },
                {
                  key: 'value',
                  align: 'right',
                  weight: 'bold',
                  header: formatMessage({ id: title }),
                },
              ];

              return [
                {
                  showHeader: true,
                  records,
                  columns,
                },
              ];
            },
            valueFormat: (value) => {
              const percent = getPercentageInfo(value, pieTotalData, 2);
              return `${isNaN(value) ? '-' : formatValue(value)}(${percent})`;
            },
          }),
      },
      title: {
        text: `{name|${formatValue(pieTotalData)}}`,
        top: '43%',
        left: 'center',
        textStyle: {
          rich: {
            name: {
              fontSize: 24,
              fontFamily: 'num-m',
              color: '#000000',
              padding: [10, 0],
            },
          },
        },
      },
      graphic: [
        {
          type: 'text',
          left: 'center',
          top: '34%',
          style: {
            fill: '#444444',
            fontSize: 12,
          },
        },
      ],
    };

    if (chartType !== 'pie') {
      const { title, graphic, series, ...restOptions } = options;
      const dataY = pieData?.map((item: any) => item.name) || [];
      const type = byType.replace('by_', '');
      const maxLabelWidth =
        Math.max(...(measureTextWidth(dataY, byType) || [])) || 60;
      const rounded = Math.round(maxLabelWidth / 10) * 10;
      const width = (rounded > maxLabelWidth ? rounded : rounded + 10) + 15;

      const rich = dataY.reduce((acc: any, item: any) => {
        const { img } = channelInfo(item);
        acc[dealwithChannelChartKey(item)] = {
          width: 16,
          height: 16,
          backgroundColor: { image: img },
        };
        return acc;
      }, {});

      return {
        ...restOptions,
        series: {
          ...series,
          label: {
            show: true,
            position: 'right',
            formatter: (params: any) => formatValue(params.value),
            fontSize: 12,
          },
          labelLayout: (params) => params,
          barMaxWidth: 80,
        },
        xAxis: {
          type: 'value',
          axisLabel: {
            formatter: (value: any) => echartsContent(value),
            color: '#838697',
            fontSize: 12,
          },
          axisLine: {
            show: true,
            lineStyle: { color: '#E5E5E5', type: 'solid' },
          },
          splitLine: {
            show: true,
            lineStyle: { color: '#E5E5E5', type: 'dashed' },
          },
        },
        yAxis: [
          {
            type: 'category',
            position: 'left',
            axisLabel: {
              interval: 0,
              show: byType === 'by_channel',
              formatter: (value: any) => `{${dealwithChannelChartKey(value)}|}`,
              rich,
              width: 20,
              align: 'right',
              padding: [0, width + 5, 0, 0],
            },
            splitLine: {
              show: false,
              lineStyle: { color: '#E5E5E5', type: 'dashed' },
            },
            axisLine: {
              show: false,
            },
            axisTick: {
              show: false,
            },
            data: dataY,
            inverse: true,
          },
          {
            type: 'category',
            position: 'left',
            axisLabel: {
              interval: 0,
              show: true,
              align: 'right',
              formatter: (value: any) => {
                if (byType === 'by_channel' && value !== 'Others') {
                  const { name } = channelInfo(value);
                  return `{label|${name}}`;
                }
                if (byType === 'by_language') {
                  return `{label|${dealWithOthersName(value, 'language')}}`;
                }
                const label = getSpecialLabelName(value);

                return `{label|${formatMessage({ id: label })}}`;
              },
              rich: {
                label: {
                  align: 'left',
                },
              },
              padding: [0, 0, 0, 0],
              overflow: 'break',
              fontWeight: 400,
              fontSize: 12,
              lineHeight: 16,
              width,
            },
            splitLine: {
              show: false,
              lineStyle: { color: '#E5E5E5', type: 'dashed' },
            },
            axisLine: {
              show: true,
              lineStyle: { color: '#E5E5E5', type: 'solid' },
            },
            axisTick: {
              show: false,
            },
            data: dataY,
            inverse: true,
          },
        ],
        grid: {
          right: '5%',
          bottom: 5,
          contentLabel: true,
        },
      };

    }

    return options;
  }, [chartType, formatMessage, data]);

  return (
    <EChartCommon
      optionsProps={{ type: chartRealType }}
      height={height}
      options={options}
      ref={eChartsRef}
    ></EChartCommon>
  );
};